import {
  Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef, ComponentRef, ViewContainerRef,
  ViewChild, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import * as _ from 'lodash';


// data, models, helpers
import { Segment } from 'shared/segments-navigation/segment.interface';
import { SearchModel } from 'shared/search/search.model';
import { getExtInfoScoreData } from 'shared/product/product-helpers';
import { getVcdbFiltersMap, getVehicleFiltersMap, getPartAttributesFiltersMap, getFilterPostObject } from 'shared/search/search-helpers';
import { VEHICLE_FILTER_TYPE, VCDB_FILTER_TYPE, PART_ATTRIBUTES_FILTER_TYPE } from 'shared/search/active-filters/active-filter-types';

// components

// services
import { DynamicComponentLoader } from 'shared/base/dynamic-component-loader.service';
import { BrandService } from 'shared/brand/brand.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import {finalize, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'segments-container',
  templateUrl: 'segments-container.component.html',
  styleUrls: ['segments-container.component.scss'],
  providers: [DynamicComponentLoader],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentsContainerComponent implements OnDestroy, OnChanges {

  @Input() itemId: any;
  @Input() segmentsList: Array<Segment> = [];
  @Input() editMode: boolean = false;
  @Input() scoreData: any = {};
  @Input() scorecardData: any = {};
  @Input() itemDetail: any = {};
  @Input() priceSheets: any = [];

  // bulk edit inputs
  @Input() bulkEdit: boolean = false;
  @Input() selectedIds: Array<any>;
  @Input() searchModel: SearchModel;
  @Input() listId: string;
  @Input() allSelected: boolean;

  @Output() changesSaved: EventEmitter<any> = new EventEmitter<any>();
  @Output() changesCanceled: EventEmitter<any> = new EventEmitter<any>();
  @Output() startChangesSaving: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('segmentContainer', { read: ViewContainerRef, static: false })
  segmentContainer: ViewContainerRef | void;

  componentRef: ComponentRef<any>;
  currentSegment: Segment;
  saving: boolean = false;

  private isActive: boolean = true;

  constructor(
    protected router: Router,
    private route: ActivatedRoute,
    private dynamicComponentLoader: DynamicComponentLoader,
    private brandService: BrandService,
    private itemDetailService: ItemDetailService,
    private ref: ChangeDetectorRef,
  ) {}

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get isDirty(): boolean {
    return _.get(this.activeComponent, 'isDirty', false);
  }

  get activeComponent(): any {
    return _.get(this.componentRef, 'instance');
  }

  get activeComponentSegmentName(): string {
    return _.get(this.activeComponent, 'segmentName');
  }

  get activeComponentFormData(): any {
    if (!this.activeComponent) { return null; }
    return this.activeComponent.getSegmentFormData();
  }

  get activeComponentHasWarning(): boolean {
    return _.get(this.activeComponent, 'hasWarning');
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnChanges(changes: SimpleChanges) {
    if (this.activeComponent) {
      if (!_.isNil(_.get(changes, 'editMode.currentValue'))) {
        this.activeComponent.toggleViewEditMode(changes.editMode.currentValue);
      }
      // if (!_.isNil(_.get(changes, 'scoreData.currentValue'))) {
      //   this.updateSegmentScoreData(changes.scoreData.currentValue);
      // }
      // if (!_.isNil(_.get(changes, 'itemDetail.currentValue'))) {
      //   this.updateSegmentItemDetail(changes.itemDetail.currentValue);
      // }
      // if (!_.isNil(_.get(changes, 'priceSheets.currentValue'))) {
      //   this.updateSegmentPriceSheets(changes.priceSheets.currentValue);
      // }
    }
  }

  ngOnDestroy() {
    this.isActive = false;
    this.destroySegment();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onForceSave() {
    this.saveComponentChanges();
  }

  onForceCancel() {
    this.saving = false;
    this.changesCanceled.emit();
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public selectSegment(segment: Segment|string): void {
    if (_.isString(segment)) {
      segment = _.find(this.segmentsList, ['name', segment]);
    }
    if (!segment) {
      console.warn('Segment not found');
      return;
    }
    this.renderSegment(segment);
  }

  public updateSegmentScoreData(scoreData: any, scorecardData: any): void {
    this.activeComponent.updateScoreData(scoreData, scorecardData);
  }

  public updateSegmentItemDetail(itemDetail: any): void {
    this.activeComponent.updateItemDetail(itemDetail);
  }

  public updateSegmentPriceSheets(priceSheets: Array<any>): void {
    this.activeComponent.updatePriceSheets(priceSheets);
  }

  public updateSegmentData(
    itemDetail?: any,
    scoreData?: any,
    scorecardData?: any,
    priceSheets?: any,
  ) {
    if (this.activeComponent) {
      this.activeComponent.updateData(
        itemDetail,
        scoreData,
        scorecardData,
        priceSheets,
      );
    }
  }

  public saveChanges(): void {
    if (this.activeComponentHasWarning) {
      this.activeComponent.showWarning();
      return;
    }
    if (this.bulkEdit) {
      // this.confirmBulkEditsModal.show();
    } else {
      this.saveComponentChanges();
    }
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private navigateToFitments(): void {
    const { id } = this.route.snapshot.params;
    this.router.navigate([`/fitments/${id}`]);
  }

  private renderSegment(segment: Segment): void {
    if (!segment) {
      console.warn('Segment not found');
      return;
    }
    this.unsubscribeSegment();
    this.componentRef = null;
    this.componentRef = this.dynamicComponentLoader.load(
      segment.component,
      this.segmentContainer,
      {
        bulkEdit: this.bulkEdit,
        selectedIds: this.selectedIds,
        searchModel: this.searchModel,
        listId: this.listId,
        allSelected: this.allSelected,
        editMode: this.editMode,
        scoreData: this.scoreData,
        scorecardData: this.scorecardData,
        itemDetail: this.itemDetail,
        priceSheets: this.priceSheets,
      }
    );

    if (segment.name === 'item-details') {
      this.componentRef.instance.forceSave.subscribe(this.onForceSave.bind(this));
    }
    if (segment.name === 'digital-assets') {
      this.componentRef.instance.forceSave.subscribe(this.onForceSave.bind(this));
      this.componentRef.instance.forceCancel.subscribe(this.onForceCancel.bind(this));
    }
    this.currentSegment = segment;
    this.ref.markForCheck();
  }

  private destroySegment(): void {
    this.dynamicComponentLoader.destroy();
    this.componentRef = null;
  }

  private unsubscribeSegment(): void {
    if (_.has(this, 'componentRef.instance.forceSave')) {
      this.componentRef.instance.forceSave.unsubscribe(this.onForceSave);
    }
    if (_.has(this, 'componentRef.instance.forceCancel')) {
      this.componentRef.instance.forceCancel.unsubscribe(this.onForceCancel);
    }
  }

  private saveComponentChanges() {
    if (this.bulkEdit) {
      this.activeComponent.storeBulkData();
    }
    if (this.activeComponentSegmentName === 'fitments') {
      // Fitments segment doesn't have save request
      // Just refresh itemDetail record
      this.changesSaved.emit();
      return;
    }
    this.sendSaveRequest()
    .pipe(finalize(() => {
      this.saving = false;
      this.ref.markForCheck();
    }), takeWhile(() => this.isActive))
    .subscribe(() => {
      if (this.activeComponent.onSavedCallback) {
        this.activeComponent.onSavedCallback();
      }
      this.changesSaved.emit();
    });
  }

  private sendSaveRequest(): Observable<any> {
    this.saving = true;
    this.startChangesSaving.emit();
    if (this.bulkEdit) {
      return this.putDataBulk();
    } else {
      return this.putData();
    }
  }

  private putData(): Observable<any> {
    return this.itemDetailService.putSegmentData(
      this.brandId,
      this.itemId,
      this.activeComponentSegmentName,
      this.activeComponentFormData,
    );
  }

  private putDataBulk(): Observable<any> {
    let filter: any = {};
    if (this.allSelected) {
      filter.searchValue = this.searchModel.searchValue;
      filter.status = (this.searchModel.statusModel) ? this.searchModel.statusModel.id.toString() : '';
      filter.searchId = (this.searchModel.searchId) ? this.searchModel.searchId.toString() : '';
      filter = _.assign(filter, getFilterPostObject(this.searchModel.filters));
    }

    return this.itemDetailService.postSegmentDataBulk(
      this.brandId,
      this.selectedIds,
      this.activeComponentSegmentName,
      this.activeComponentFormData,
      (this.allSelected ? filter : null),
      (this.allSelected ? this.listId : '')
    );
  }
}
